import { useState } from "react";
import { ChevronDown, Coins, Crown } from "lucide-react";
import { useStore } from "@/store/useStore";
import { GOLD_PURITIES, SILVER_PURITIES, SILVER_UNITS } from "@/lib/constants";
import { formatPurity } from "@/lib/formatPurity";
import { useHydrationSafe } from "@/hooks/useHydrationSafe";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export function NavMetalPuritySelector() {
  const { isHydrated } = useHydrationSafe();
  const [open, setOpen] = useState(false);
  const { 
    goldPurity, 
    silverPurity, 
    silverUnit, 
    setGoldPurity, 
    setSilverPurity, 
    setSilverUnit 
  } = useStore();

  // Determine which metal to show (could be based on last used or user preference)
  const [activeMetal, setActiveMetal] = useState<'gold' | 'silver'>('gold');

  // 🛡️ Pre-hydration: render static placeholder matching SSR default (gold/24KT)
  if (!isHydrated) {
    return (
      <div className="hidden lg:flex">
        <button 
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted/50 dark:bg-gray-800/50 border border-border/50 shadow-sm ring-1 ring-gold/20 bg-gold/5"
          aria-label="Seleziona metallo e purezza"
          disabled
        >
          <Crown className="h-4 w-4 text-gold" />
          <span className="text-gold">24KT</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </div>
    );
  }

  const getCurrentPurityLabel = () => {
    if (activeMetal === 'gold') {
      return formatPurity('gold', goldPurity);
    } else {
      return formatPurity('silver', silverPurity) + (silverUnit === 'kg' ? '/kg' : '/g');
    }
  };

  const getMetalIcon = () => {
    return activeMetal === 'gold' ? (
      <Crown className="h-4 w-4 text-gold" />
    ) : (
      <Coins className="h-4 w-4 text-silver" />
    );
  };

  const handleMetalChange = (metal: 'gold' | 'silver') => {
    setActiveMetal(metal);
  };

  return (
    <div className="hidden lg:flex">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button 
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium
              transition-all duration-300 hover:scale-105
              bg-muted/50 hover:bg-muted/70 dark:bg-gray-800/50 dark:hover:bg-gray-700/50
              border border-border/50 hover:border-border
              shadow-sm hover:shadow-md
              ${activeMetal === 'gold' ? 'ring-1 ring-gold/20 bg-gold/5' : 'ring-1 ring-silver/20 bg-silver/5'}
            `}
            aria-label="Seleziona metallo e purezza"
          >
            {getMetalIcon()}
            <span className={activeMetal === 'gold' ? 'text-gold' : 'text-silver'}>
              {getCurrentPurityLabel()}
            </span>
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        </PopoverTrigger>

        <PopoverContent 
          className="w-80 p-0 shadow-xl border-border/50"
          align="end"
          sideOffset={8}
        >
          <div className="p-4 space-y-4">
            {/* Metal Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-foreground">Metallo</Label>
              <RadioGroup
                value={activeMetal}
                onValueChange={(value: string) => handleMetalChange(value as 'gold' | 'silver')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="gold" 
                    id="gold"
                    className="border-gold data-[state=checked]:border-gold data-[state=checked]:bg-gold/10"
                  />
                  <Label 
                    htmlFor="gold" 
                    className="flex items-center gap-2 cursor-pointer text-gold font-medium"
                  >
                    <Crown className="h-4 w-4" />
                    Oro
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem 
                    value="silver" 
                    id="silver"
                    className="border-silver data-[state=checked]:border-silver data-[state=checked]:bg-silver/10"
                  />
                  <Label 
                    htmlFor="silver" 
                    className="flex items-center gap-2 cursor-pointer text-silver font-medium"
                  >
                    <Coins className="h-4 w-4" />
                    Argento
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Divider */}
            <div className="border-t border-border/30" />

            {/* Purity Selection */}
            {activeMetal === 'gold' ? (
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gold">Purezza Oro</Label>
                <Select value={goldPurity} onValueChange={setGoldPurity}>
                  <SelectTrigger className="border-gold/30 focus:border-gold focus:ring-gold/20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(GOLD_PURITIES).map(([key, { label }]) => (
                      <SelectItem 
                        key={key} 
                        value={key}
                        className="focus:bg-gold/10 focus:text-gold"
                      >
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-silver">Purezza Argento</Label>
                  <Select value={silverPurity} onValueChange={setSilverPurity}>
                    <SelectTrigger className="border-silver/30 focus:border-silver focus:ring-silver/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SILVER_PURITIES).map(([key, { label }]) => (
                        <SelectItem 
                          key={key} 
                          value={key}
                          className="focus:bg-silver/10 focus:text-silver"
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-semibold text-silver">Unità</Label>
                  <Select value={silverUnit} onValueChange={setSilverUnit}>
                    <SelectTrigger className="border-silver/30 focus:border-silver focus:ring-silver/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SILVER_UNITS.map((unit) => (
                        <SelectItem 
                          key={unit} 
                          value={unit}
                          className="focus:bg-silver/10 focus:text-silver"
                        >
                          {unit === 'g' ? 'Grammi (g)' : 'Chilogrammi (kg)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Footer with quick info */}
            <div className="pt-2 border-t border-border/30">
              <p className="text-xs text-muted-foreground text-center">
                Le preferenze si applicano a tutto il sito
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}